import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useUser } from '../hooks/useUser.ts';

type Role =
  | 'admin'
  | 'manager'
  | 'operator'
  | 'viewer'
  | 'superadmin'
  | 'director'
  | 'operation'
  | 'finance_manager'
  | 'logistics';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Required roles. If omitted, any authenticated user passes. */
  roles?: readonly Role[];
  /**
   * Required permission from the editable matrix. Preferred over `roles` —
   * the server gates on the same key, so the two can't drift.
   */
  permission?: string;
}

export function ProtectedRoute({ children, roles, permission }: ProtectedRouteProps) {
  const { user, isLoading } = useUser();
  const location = useLocation();

  if (isLoading) return <div className="loading">Loading…</div>;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permission gate wins when given. The server enforces the same key, so this
  // only controls whether the page is offered — never whether data is exposed.
  const perms = (user.permissions as string[] | undefined) ?? null;
  if (permission && perms && !perms.includes(permission)) {
    return <Navigate to="/" replace />;
  }

  // A user may hold several roles; pass if ANY of them is allowed. Falls back
  // to the primary role so a session predating multi-role still works.
  const held: Role[] = ((user.roles as Role[] | undefined) ?? [user.role as Role]);
  if (!permission && roles && !held.some((r) => roles.includes(r))) {
    // Hide rather than expose: silently redirect to home. The Layout
    // sidebar already excludes links the role can't see, so users
    // typically only hit this branch by typing a URL directly.
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
